import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { addEmployee, getRoles, getUserById, updateUser } from "../Auth";
import { getEmployeeSchema } from "../utils/employeeSchema";
import { parseIsoDateToMDYUS } from "../utils/dateUtils";
import toast from "react-hot-toast";
import {
  INACTIVE_TO_ACTIVE_REQUIRES_SUPPORT_MESSAGE,
  isEmployeeInactiveStatus,
} from "../constants/employeeStatus";
import { requireSessionTenantId } from "../utils/tenantContext";

export const useAddEmployee = (
  onSuccess,
  defaultRoleName,
  employeeId = null,
  adminFieldsEnabled = false,
) => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false); // For initial data load
    const [error, setError] = useState("");
    const [initialStatus, setInitialStatus] = useState("Active");

    const isUpdate = !!employeeId;
    const isInactiveEmployee = isUpdate && isEmployeeInactiveStatus(initialStatus);

    const schema = useMemo(() => getEmployeeSchema(isUpdate), [isUpdate]);

    const form = useForm({
        resolver: zodResolver(schema),
        shouldUseNativeValidation: false,
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            roleId: "",
            status: "Active",
            workMode: "",
            hireDate: "",
            dateOfBirth: "",
            ssn: "",
            mobileNumber: "",
            payRate: "",
            personalNotes: "",
            address: "",
            city: "",
            state: "",
            zipCode: "",
            country: "USA",
        },
    });

    // Load roles
    useEffect(() => {
        const loadRoles = async () => {
            try {
                const data = await getRoles();
                setRoles(data);
            } catch (err) {
                console.error("Failed to load roles", err);
            }
        };
        loadRoles();
    }, []);

    // Load existing employee data if employeeId is present
    useEffect(() => {
        if (!employeeId) return;

        const fetchEmployee = async () => {
            setFetching(true);
            try {
                const response = await getUserById(employeeId);
                // Handle response structure: { result: { user: { ... } } }
                const user = response?.result?.user || response?.result || response;

                if (user) {
                    // Map API data to form fields
                    const formData = {
                        firstName: user.firstName || "",
                        lastName: user.lastName || "",
                        email: user.email || "",
                        roleId: user.roleId || "",
                        status: user.status || "Active",
                        workMode: user.workMode || "WFO",
                        dateOfBirth: parseIsoDateToMDYUS(user.dateOfBirth),
                        ssn: user.ssn || "",
                        mobileNumber: user.mobileNumber || "",
                        hireDate: parseIsoDateToMDYUS(user.hireDate),
                        address: user.address || "",
                        city: user.city || "",
                        state: user.state || "",
                        zipCode: user.zipCode || "",
                        country: user.country || "",
                    };

                    if (adminFieldsEnabled) {
                        formData.payRate = user.payRate || "";
                        formData.personalNotes = user.personalNotes || "";
                    }

                    setInitialStatus(user.status || "Active");
                    // Reset form with fetched data
                    // We use reset() instead of setValue loop to ensure dirty state is clean initially
                    form.reset(formData);
                }
            } catch (err) {
                setError("Failed to load employee details");
                console.error(err);
            } finally {
                setFetching(false);
            }
        };

        fetchEmployee();
    }, [employeeId, form, adminFieldsEnabled]);

    const stripAdminOnlyFields = (payload) => {
        if (adminFieldsEnabled) return payload;
        const next = { ...payload };
        delete next.payRate;
        delete next.personalNotes;
        return next;
    };

    // Effect to pre-select role (Only if NOT updating)
    useEffect(() => {
        if (!employeeId && roles.length > 0 && defaultRoleName && defaultRoleName !== "All") {
            const matchingRole = roles.find(r => r.code === defaultRoleName || r.name === defaultRoleName);
            if (matchingRole) {
                form.setValue("roleId", matchingRole.id, { shouldValidate: true });
            }
        }
    }, [roles, defaultRoleName, form, employeeId]);

    const onSubmit = async (data) => {
        setLoading(true);
        setError("");
        try {
            const tenantId = requireSessionTenantId();

            if (employeeId) {
                // Update existing employee
                // Explicitly remove password from update payload to avoid security risks/overwriting
            const updateData = stripAdminOnlyFields({
                  ...data,
                  tenantId,
                  email: String(data.email || "")
                    .trim()
                    .toLowerCase(),
                });
                delete updateData.password;

                if (
                    isEmployeeInactiveStatus(initialStatus) &&
                    !isEmployeeInactiveStatus(updateData.status)
                ) {
                    toast.error(INACTIVE_TO_ACTIVE_REQUIRES_SUPPORT_MESSAGE);
                    setLoading(false);
                    return;
                }

                const response = await updateUser(employeeId, updateData);
                if (response.status === 1) {
                    toast.success(response.message)
                    form.reset(data);
                    setInitialStatus(data.status || initialStatus);
                    if (onSuccess) onSuccess();
                } else {
                    toast.error(response.message)
                }
            } else {
                const response = await addEmployee(stripAdminOnlyFields({
                    ...data,
                    tenantId,
                    email: String(data.email || "")
                      .trim()
                      .toLowerCase(),
                }));
                if (response.status === 1) {
                    toast.success(response.message)
                    form.reset(data);
                    if (onSuccess) onSuccess();
                } else {
                    toast.error(response.message)
                }
            }
        } catch (err) {
            setError(err.message || `Failed to ${employeeId ? "update" : "add"} employee`);
        } finally {
            setLoading(false);
        }
    };

    return {
        form,
        roles,
        loading: loading || fetching, // Block UI while fetching initial data too
        /** True only while a submit request is in flight (not initial employee fetch). */
        isSubmitting: loading,
        error,
        onSubmit: form.handleSubmit(onSubmit),
        /** Pass `onInvalid` to run when validation fails (e.g. clear a pending navigation flag). */
        submitForm: (onInvalid) => form.handleSubmit(onSubmit, onInvalid),
        isUpdate: !!employeeId,
        isInactiveEmployee,
    };
};
